/**
 * Rooms — per-project group chat for employees.
 *
 * Left: the projects this employee is assigned to (one room each).
 * Right: the selected room's message feed + composer.
 *
 * Polling, not websockets (matches the app's polling+optimistic model): the
 * message feed refetches on an interval while a room is open. Employee-only —
 * the sidebar hides the tab from admins and the backend 403s them.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
} from "react";

import { Avatar } from "@m2s/design-system";

import { api, extractErrorMessage } from "@/lib/api";
import type { Room, RoomMessage } from "@/lib/types";

function initials(name: string | null): string {
  if (!name) return "?";
  const p = name.trim().split(/\s+/);
  return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[p.length - 1][0]).toUpperCase();
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function RoomsPage() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  const roomsQuery = useQuery({
    queryKey: ["rooms"],
    queryFn: async () => (await api.get<Room[]>("/me/rooms")).data,
    refetchInterval: 15000,
  });
  const rooms = useMemo(() => roomsQuery.data ?? [], [roomsQuery.data]);

  // Default to the first room once loaded.
  useEffect(() => {
    if (selected === null && rooms.length > 0) setSelected(rooms[0].project_id);
  }, [rooms, selected]);

  const messagesQuery = useQuery({
    queryKey: ["room-messages", selected],
    queryFn: async () =>
      (await api.get<RoomMessage[]>(`/me/rooms/${selected}/messages`)).data,
    enabled: selected !== null,
    refetchInterval: 4000, // poll for new messages
  });
  const messages = messagesQuery.data ?? [];

  // Auto-scroll to newest on message change.
  useEffect(() => {
    const el = feedRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, selected]);

  const send = useMutation({
    mutationFn: async (body: string) =>
      (await api.post<RoomMessage>(`/me/rooms/${selected}/messages`, { body })).data,
    onSuccess: () => {
      setDraft("");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["room-messages", selected] });
      queryClient.invalidateQueries({ queryKey: ["rooms"] });
    },
    onError: (err) => setError(extractErrorMessage(err, "Could not send your message.")),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  };

  const activeRoom = rooms.find((r) => r.project_id === selected) ?? null;

  return (
    <div>
      <header style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.3px", margin: 0 }}>
          Rooms
        </h1>
        <p style={{ color: "var(--mute)", fontSize: 13, marginTop: 6 }}>
          Group chat for the projects you're on. Everyone assigned to a project
          shares its room.
        </p>
      </header>

      {roomsQuery.isLoading ? (
        <p style={{ color: "var(--mute)" }}>Loading…</p>
      ) : rooms.length === 0 ? (
        <div style={emptyCard}>
          You're not assigned to any project yet. Once an admin assigns you to a
          project, its room shows up here.
        </div>
      ) : (
        <div style={shell}>
          {/* Room list */}
          <aside style={roomList}>
            {rooms.map((r) => {
              const active = r.project_id === selected;
              return (
                <button
                  key={r.project_id}
                  type="button"
                  onClick={() => setSelected(r.project_id)}
                  style={roomItem(active)}
                >
                  <span aria-hidden style={{ ...swatch, background: r.color }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontWeight: 550, fontSize: 13.5, color: "var(--ink)" }}>
                        {r.project_name}
                      </span>
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--mute)" }}>
                        {r.project_key}
                      </span>
                    </span>
                    <span style={preview}>
                      {r.last_message_preview ?? "No messages yet"}
                    </span>
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Conversation */}
          <section style={convo}>
            {activeRoom && (
              <div style={convoHeader}>
                <span aria-hidden style={{ ...swatch, background: activeRoom.color }} />
                <span style={{ fontWeight: 600, fontSize: 14 }}>{activeRoom.project_name}</span>
                <span style={{ fontSize: 12, color: "var(--mute)" }}>
                  {activeRoom.member_count} member{activeRoom.member_count === 1 ? "" : "s"}
                </span>
              </div>
            )}

            <div ref={feedRef} style={feed}>
              {messagesQuery.isLoading ? (
                <p style={{ color: "var(--mute)", fontSize: 13 }}>Loading messages…</p>
              ) : messages.length === 0 ? (
                <p style={{ color: "var(--mute)", fontSize: 13, margin: "auto" }}>
                  No messages yet. Say hello.
                </p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} style={msgRow(m.is_mine)}>
                    {!m.is_mine && (
                      <Avatar initials={initials(m.author_display_name)} title={m.author_display_name ?? ""} />
                    )}
                    <div style={bubble(m.is_mine)}>
                      {!m.is_mine && (
                        <div style={{ fontSize: 11.5, fontWeight: 550, color: "var(--body-mid)", marginBottom: 2 }}>
                          {m.author_display_name ?? "Unknown"}
                        </div>
                      )}
                      <div style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {m.body}
                      </div>
                      <div style={{ fontSize: 10.5, color: m.is_mine ? "var(--on-primary)" : "var(--mute)", opacity: m.is_mine ? 0.7 : 1, marginTop: 4, textAlign: "right" }}>
                        {fmtTime(m.created_at)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {error && (
              <div role="alert" style={errorBar}>{error}</div>
            )}

            <form onSubmit={onSubmit} style={composer}>
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSubmit(e);
                  }
                }}
                placeholder="Write a message…  (Enter to send, Shift+Enter for newline)"
                rows={2}
                maxLength={4000}
                style={composerInput}
              />
              <button
                type="submit"
                disabled={send.isPending || !draft.trim()}
                style={sendBtn(send.isPending || !draft.trim())}
              >
                {send.isPending ? "Sending…" : "Send"}
              </button>
            </form>
          </section>
        </div>
      )}
    </div>
  );
}

// ---------- styles ----------

const shell: CSSProperties = {
  display: "flex",
  gap: 16,
  height: "calc(100vh - 220px)",
  minHeight: 420,
};

const roomList: CSSProperties = {
  width: 280,
  flexShrink: 0,
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  background: "var(--canvas)",
  overflowY: "auto",
  padding: 6,
};

function roomItem(active: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "10px 10px",
    border: "none",
    borderRadius: 6,
    background: active ? "var(--surface-2)" : "transparent",
    cursor: "pointer",
    marginBottom: 2,
  };
}

const swatch: CSSProperties = { width: 10, height: 10, borderRadius: 3, flexShrink: 0, marginTop: 4 };

const preview: CSSProperties = {
  display: "block",
  fontSize: 12,
  color: "var(--mute)",
  marginTop: 2,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: 220,
};

const convo: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  background: "var(--canvas)",
  overflow: "hidden",
};

const convoHeader: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 16px",
  borderBottom: "1px solid var(--hairline)",
  background: "var(--surface-1)",
};

const feed: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

function msgRow(mine: boolean): CSSProperties {
  return {
    display: "flex",
    gap: 8,
    alignItems: "flex-end",
    flexDirection: mine ? "row-reverse" : "row",
  };
}

function bubble(mine: boolean): CSSProperties {
  return {
    maxWidth: "70%",
    padding: "8px 12px",
    borderRadius: 10,
    background: mine ? "var(--ink)" : "var(--surface-2)",
    color: mine ? "var(--on-primary)" : "var(--ink)",
  };
}

const composer: CSSProperties = {
  display: "flex",
  gap: 8,
  padding: 12,
  borderTop: "1px solid var(--hairline)",
  alignItems: "flex-end",
};

const composerInput: CSSProperties = {
  flex: 1,
  resize: "none",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  padding: "8px 10px",
  fontSize: 13.5,
  fontFamily: "var(--font-sans)",
  outline: "none",
  background: "var(--canvas)",
  color: "var(--ink)",
};

function sendBtn(disabled: boolean): CSSProperties {
  return {
    background: "var(--ink)",
    color: "var(--on-primary)",
    border: "none",
    borderRadius: 4,
    padding: "9px 18px",
    fontSize: 13.5,
    fontWeight: 550,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.55 : 1,
    fontFamily: "var(--font-sans)",
    alignSelf: "stretch",
  };
}

const emptyCard: CSSProperties = {
  border: "1px dashed var(--hairline)",
  borderRadius: 8,
  padding: 24,
  color: "var(--mute)",
  fontSize: 14,
  background: "var(--surface-1)",
  maxWidth: 560,
};

const errorBar: CSSProperties = {
  margin: "0 12px",
  padding: "8px 12px",
  fontSize: 13,
  color: "#ee1d36",
  background: "rgba(238,29,54,0.06)",
  border: "1px solid rgba(238,29,54,0.3)",
  borderRadius: 4,
};
